import posthog from 'posthog-js'

/**
 * Shared PostHog custom-event helper (AB#4330).
 *
 * Application Insights was removed in favour of PostHog. Route-level page
 * views are already covered by the `$pageview` capture in `posthog.ts`
 * (`router.afterEach`), but the named diagnostic/business events that used to
 * go to App Insights (`UserAuthenticated`, `EnvironmentLoaded`,
 * `LoginFailure`, MSAL errors, ...) need explicit equivalents — this module
 * is the single place to send them from.
 *
 * The helper never throws and never blocks: analytics must not break the app,
 * and it is safe to call before PostHog has initialised (e.g. no client key
 * configured) or when the user has not consented to statistics.
 */
export function capturePosthogEvent(event: string, properties?: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties)
  } catch {
    // Intentionally ignored — telemetry failures stay silent.
  }
}

/**
 * Report errors that the application intentionally catches or that Vue handles
 * before they reach the browser's global error handlers.
 */
export function capturePosthogException(
  error: unknown,
  properties?: Record<string, unknown>
): void {
  try {
    posthog.captureException(error, properties)
  } catch {
    // Intentionally ignored — telemetry failures stay silent.
  }
}

/**
 * Serialise an unknown caught error into a string safe for use as a PostHog
 * event property. PostHog properties must be primitives, so raw `Error`
 * objects (or arbitrary rejection values) are reduced to their message.
 */
export function toErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  try {
    const serialised = JSON.stringify(error)
    return typeof serialised === 'string' ? serialised : String(error)
  } catch {
    return String(error)
  }
}
